import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createBloodGlucose, updateBloodGlucose } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { debounce } from '../../../../utils/debounce';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { formatFloat } from '../../../../utils/numberUtils';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { HEALTH_INCIDENT_LEVEL, RANGE_WARNING_CONTENT, validate } from './validateBloodGlucose';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _WARNING_INTERVAL = 1000;

const EditBloodGlucose = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant blood glucose measurement has been ${
        formType === 'create' ? 'added' : 'updated'
    }`;

    const initialState = {
        id: '',
        participantId: '',
        eventDate: todayDate(),
        measurementValue: '',
        measurementId: '',
        measurement: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [showWarning, setShowWarning] = useState(false);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );

    // USEEFFECTS
    useEffect(() => {
        return () => configWarning.current.cancel?.();
    }, []);

    useEffect(() => {
        if (Object.keys(row).length < 1 || formType !== 'edit') return;
        setNewEntry({ ...row });
        setShowWarning(row.measurementValue > HEALTH_INCIDENT_LEVEL);
    }, [row, formType]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const configWarning = useRef(
        debounce(
            (value) => setShowWarning(value ? value > HEALTH_INCIDENT_LEVEL : false),
            _WARNING_INTERVAL
        )
    );

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onGlucoseValueChange = (e) => {
        const { value } = e.target;
        onUpdate('measurementValue', formatFloat(value));
        configWarning.current(value);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry, row, formType);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        // eslint-disable-next-line
        const { measurement, isWarning, ...rest } = newEntry;
        const payload = { ...rest, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createBloodGlucose(payload, _MESSAGE))
            : dispatch(updateBloodGlucose(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_blood_glucose_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Blood Glucose' : 'Edit Blood Glucose'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id="eventDate"
                            label="Event Date"
                            value={newEntry.eventDate}
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="measurementValue"
                            label="Glucose Value"
                            placeholder="Enter glucose value..."
                            mandatory={true}
                            disabled={isDisabled}
                            maxLength={4}
                            value={newEntry.measurementValue || ''}
                            error={errors.measurementValue}
                            warning={showWarning ? RANGE_WARNING_CONTENT : ''}
                            onChange={onGlucoseValueChange}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="measurementId"
                            label="Measurement"
                            placeholder="Select measurement type..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={measurementTypes || []}
                            selectedId={newEntry.measurementId}
                            selected={getObjFromId(measurementTypes, newEntry.measurementId)}
                            error={errors.measurementId}
                            onChange={(chosenId) => onUpdate('measurementId', chosenId)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditBloodGlucose.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default EditBloodGlucose;
